import React, { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { toJalaliParts, toGregorianDate } from '../utils/jalali';

const MONTHS = ['Hamal','Saur','Jawza','Saratan','Asad','Sumbula','Mizan','Aqrab','Qaws','Jadi','Dalwa','Hut'];
const DAYS   = ['Su','Mo','Tu','We','Th','Fr','Sa'];

function jDaysInMonth(jYear, jMonth1) {
  if (jMonth1 <= 6) return 31;
  if (jMonth1 <= 11) return 30;
  // Esfand: check leap year
  const nextY = toGregorianDate(jYear + 1, 1, 1);
  const thisY = toGregorianDate(jYear, 1, 1);
  const diff  = Math.round((nextY - thisY) / 86400000);
  return diff > 365 ? 30 : 29;
}

export default function JalaliDatePicker({ value, onChange, placeholder = 'Select date', className = '', disabled = false }) {
  const [open, setOpen]    = useState(false);
  const [jYear, setJYear]  = useState(null);
  const [jMonth, setJMonth]= useState(null); // 0-indexed
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const initCalendar = () => {
    const base = value ? toJalaliParts(new Date(value)) : toJalaliParts(new Date());
    if (base) { setJYear(base.jy); setJMonth(base.jm - 1); }
  };

  const handleOpen = () => {
    if (disabled) return;
    initCalendar();
    setOpen(true);
  };

  const displayValue = value ? (() => {
    const p = toJalaliParts(new Date(value));
    return p ? `${p.jy}/${String(p.jm).padStart(2,'0')}/${String(p.jd).padStart(2,'0')}` : '';
  })() : '';

  const prevMonth = () => { let m = jMonth-1, y = jYear; if(m<0){m=11;y--;} setJMonth(m); setJYear(y); };
  const nextMonth = () => { let m = jMonth+1, y = jYear; if(m>11){m=0;y++;} setJMonth(m); setJYear(y); };

  const getDays = () => {
    if (jYear === null || jMonth === null) return [];
    const m1     = jMonth + 1;
    const total  = jDaysInMonth(jYear, m1);
    const first  = toGregorianDate(jYear, m1, 1);
    const startDow = first.getDay(); // 0=Sun
    const days = [];
    for (let i = 0; i < startDow; i++) days.push(null);
    for (let d = 1; d <= total; d++) days.push(d);
    return days;
  };

  const selectDay = (d) => {
    if (!d) return;
    const greg = toGregorianDate(jYear, jMonth + 1, d);
    const iso  = `${greg.getFullYear()}-${String(greg.getMonth()+1).padStart(2,'0')}-${String(greg.getDate()).padStart(2,'0')}`;
    onChange(iso);
    setOpen(false);
  };

  const selP = value ? toJalaliParts(new Date(value)) : null;
  const todayP = toJalaliParts(new Date());
  const days = getDays();

  return (
    <div className={`relative ${className}`} ref={ref}>
      <div onClick={handleOpen}
        className={`flex items-center border border-gray-200 rounded-lg bg-white px-3 py-2 text-sm cursor-pointer hover:border-blue-400 transition-colors
          ${disabled?'opacity-50 cursor-not-allowed':''}
          ${open?'ring-2 ring-blue-500 border-blue-500':''}`}>
        <Calendar size={14} className="text-gray-400 mr-2 flex-shrink-0"/>
        <span className={displayValue?'text-gray-800 flex-1':'text-gray-400 flex-1'}>{displayValue||placeholder}</span>
        {displayValue && (
          <button type="button" onClick={e=>{e.stopPropagation();onChange('');}} className="text-gray-300 hover:text-gray-500 ml-1">×</button>
        )}
      </div>

      {open && jYear !== null && (
        <div className="absolute top-full left-0 z-50 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl p-3 w-64">
          <div className="flex items-center justify-between mb-2">
            <button type="button" onClick={prevMonth} className="p-1 rounded hover:bg-gray-100"><ChevronLeft size={16}/></button>
            <span className="text-sm font-semibold text-gray-800">{MONTHS[jMonth]} {jYear}</span>
            <button type="button" onClick={nextMonth} className="p-1 rounded hover:bg-gray-100"><ChevronRight size={16}/></button>
          </div>
          <div className="grid grid-cols-7 mb-1">
            {DAYS.map(d=><div key={d} className="text-center text-xs text-gray-400 py-1">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-0.5">
            {days.map((d,i)=>{
              const isSel  = d && selP && d===selP.jd && jMonth===selP.jm-1 && jYear===selP.jy;
              const isToday= d && todayP && d===todayP.jd && jMonth===todayP.jm-1 && jYear===todayP.jy;
              return (
                <button key={i} type="button" onClick={()=>selectDay(d)} disabled={!d}
                  className={`h-7 w-full text-xs rounded-lg transition-colors
                    ${!d?'invisible':isSel?'bg-blue-600 text-white font-bold':isToday?'bg-blue-50 text-blue-700 font-semibold':'hover:bg-gray-100 text-gray-700'}`}>
                  {d||''}
                </button>
              );
            })}
          </div>
          <div className="flex gap-2 mt-2 pt-2 border-t border-gray-100">
            <input type="number" value={jYear} onChange={e=>setJYear(Number(e.target.value))}
              className="w-16 text-xs border border-gray-200 rounded px-2 py-1 text-center"/>
            <select value={jMonth} onChange={e=>setJMonth(Number(e.target.value))}
              className="flex-1 text-xs border border-gray-200 rounded px-1 py-1 bg-white">
              {MONTHS.map((m,i)=><option key={i} value={i}>{m}</option>)}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

export function JalaliDateRangeFilter({ from, to, onFromChange, onToChange }) {
  return (
    <>
      <JalaliDatePicker value={from} onChange={onFromChange} placeholder="From date" className="w-40"/>
      <JalaliDatePicker value={to}   onChange={onToChange}   placeholder="To date"   className="w-40"/>
    </>
  );
}
